import { useRef } from 'react';
import { logDebug, logError } from '@rs-core/utils/logger';
import useTwilioLockStore from '@rs-core/store/twilioLockStore';
import { useNumberFlagValue } from './useFlags';

/**
 * A custom hook leveraging Twilio Sync to manage a lock on a resource.
 * To ensure we can detect that a user times out for whatever reason (ie: crash, network issue, etc), we need to leverage the server-side TTL mechanism.
 * @param {Object} twilioClient - The Twilio client instance.
 * @param {string} key - The key used as the ID of the Twilio Sync document.
 * @param {Object} loggedInUser - The user object of the currently logged in user.
 * @param {string} id - A unique userid used to detect - it should be something other than internal user id in case users are logged into multiple computers.
 * @param {Function} [onUpdated] - Optional callback when lock state changes.
 * @param {boolean} initialize - Flag to initialize the document.
 * @returns {Object} - An object containing lock state, lock holder, error, and functions to acquire and release the lock.
 * @property {boolean} isLocked - Indicates if the document is currently locked.
 * @property {string|null} lockHolder - The ID of the user currently holding the lock.
 * @property {boolean} canEdit - Indicates if the current user can edit the document.
 * @property {Function} acquireLock - Function to acquire the lock on the document.
 * @property {Function} releaseLock - Function to release the lock on the document.
 */

const useTwilioLock = ({ key, id, onUpdated }) => {
	// Get timeout value from feature flag, default is 15 minutes
	const galaxyDvReportAccessTimeoutMinute = useNumberFlagValue('galaxy-dv-report-access-timeout-minute', 15);

	// Convert minutes to milliseconds for lock TTL
	const LOCK_TTL = (galaxyDvReportAccessTimeoutMinute ?? 15) * 60 * 1000;

	// Calculate warning threshold as 1/3 of TTL, rounded to 1 decimal place
	const WARNING_THRESHOLD = Math.round((LOCK_TTL / 3) * 10) / 10;

	const {
		setSyncDoc,
		getSyncDoc,
		isLocked,
		lockHolder,
		lockHolderId,
		updateLockState,
		reset,
		documentId,
		setIsInitialized,
	} = useTwilioLockStore();

	let doc = useRef(null);

	const isExpired = expiryTime => {
		const now = new Date().getTime();
		const expTime = new Date(expiryTime).getTime();

		return now - expTime > 0;
	};

	const releaseLock = async ({ unmount }) => {
		if (unmount) {
			setIsInitialized(false);
		}
		const syncDoc = getSyncDoc();

		logDebug('useTwilioLock', 'releasing lock', { key, id, data: syncDoc?.data });

		if (!syncDoc || !syncDoc.data.isLocked || syncDoc.data.lockHolderId !== id) return false;

		try {
			await syncDoc.set({
				isLocked: false,
				lockHolder: null,
				lockHolderId: null,
				expiryTime: null,
				trigger: 'releaseLock',
			});
			updateLockState();

			logDebug('useTwilioLock', 'released lock', { key, id, data: syncDoc.data });
			return true;
		} catch (err) {
			logError('useTwilioLock', 'error while releasing lock', err);
			return false;
		}
	};

	const handleOnActive = async getLastActiveTime => {
		const syncDoc = getSyncDoc();
		const lastActivityTime = getLastActiveTime();

		// Skip if no sync doc, activity time, or wrong holder
		if (!syncDoc || !lastActivityTime || syncDoc.data.lockHolderId !== id) return;

		const currentTime = new Date().getTime();
		const expiryTime = syncDoc.data.expiryTime;
		const lastActivity = new Date(lastActivityTime).getTime();

		// Calculate time differences
		const timeDifferenceWithCurrent = currentTime - lastActivity;
		const timeDifferenceWithExpire = expiryTime - lastActivity;

		logDebug('useTwilioLock', 'handleOnActive timeDifference', {
			timeDifferenceWithCurrent,
			timeDifferenceWithExpire,
		});

		// Release lock if user is idle longer than TTL
		if (timeDifferenceWithCurrent >= LOCK_TTL) {
			logDebug('useTwilioLock', 'user is idle, releasing lock');
			await releaseLock({ unmount: false });
		}

		// Update expiry time if within warning threshold
		if (timeDifferenceWithExpire <= WARNING_THRESHOLD) {
			await updateExpiryTime(lastActivityTime);
		}
	};

	const updateExpiryTime = async lastActivityTime => {
		const syncDoc = getSyncDoc();

		if (!syncDoc || !lastActivityTime || syncDoc.data.lockHolderId !== id) return false;

		const newExpiryTime = new Date(lastActivityTime).getTime() + LOCK_TTL;

		try {
			await syncDoc.set({
				...syncDoc.data,
				expiryTime: newExpiryTime,
				trigger: 'updateExpiryTime',
			});
			updateLockState();

			logDebug('useTwilioLock', 'updated expiry time', { key, id, newExpiryTime, data: syncDoc.data });

			return true;
		} catch (err) {
			logError('useTwilioLock', 'error while updating expiry time', err);
			return false;
		}
	};

	const initializeDocument = async ({ mounted, twilioClient }) => {
		try {
			// this creates the doc if it doesn't already exist
			doc.current = await twilioClient.document(key);

			logDebug('useTwilioLock', 'initializing Twilio Sync document', key, doc.current.data, mounted.current);

			if (mounted.current) {
				setSyncDoc(doc.current);
				updateLockState();
				const data = doc.current.data || {};

				logDebug('useTwilioLock', 'initialized document', key, data);
				// If the lock is expired, release it
				if (data.expiryTime && isExpired(data.expiryTime)) {
					const syncDoc = getSyncDoc();
					await syncDoc.set({
						isLocked: false,
						lockHolder: null,
						lockHolderId: null,
						expiryTime: null,
						documentId: null,
						trigger: 'releaseLock',
					});
					updateLockState();
				}
			}

			doc.current.on('updated', event => {
				if (mounted.current) {
					logDebug('useTwilioLock', 'document updated', event.data);

					onUpdated?.(event.data.isLocked, event.data.lockHolder);
					updateLockState();
				}
			});

			// We don't usually remove docs but just in case
			doc.current.on('removed', async () => {
				if (mounted.current) {
					reset();
					onUpdated?.(false, null);
				}
			});
			setIsInitialized(true);
		} catch (err) {
			if (mounted.current) {
				logError('useTwilioLock', 'error initializing document', err);
			}
		}
	};

	const acquireLock = async (user, uniqueId, startDocumentId) => {
		const syncDoc = getSyncDoc();

		logDebug('useTwilioLock', 'acquiring lock', {
			key,
			id,
			isLocked,
			startDocumentId,
		});

		if (!syncDoc || !user || !uniqueId) return false;

		const data = syncDoc.data;
		const isCurrentlyLocked = data.isLocked && data.lockHolderId !== uniqueId;
		const isLockExpired = isExpired(data.expiryTime);

		if (isCurrentlyLocked && !isLockExpired) {
			logDebug('useTwilioLock', 'found an existing lock', key, data);
			return false;
		}

		try {
			await syncDoc.set({
				isLocked: true,
				lockHolder: user?.fullName,
				lockHolderId: uniqueId,
				documentId: startDocumentId,
				expiryTime: new Date().getTime() + LOCK_TTL,
				trigger: 'acquireLock',
			});
			updateLockState();
			logDebug('useTwilioLock', 'acquired lock', key, syncDoc.data);

			return true;
		} catch (err) {
			if (err.code === 54001) {
				// 54001 means document was modified by someone else
				// The update failed because someone else modified the document first
				return false;
			}
			logError('useTwilioLock', 'error while acquiring lock', err);
			return false;
		}
	};

	const updateDocumentId = async (uniqueId, newDocumentId) => {
		const syncDoc = getSyncDoc();

		logDebug('useTwilioLock', 'updating document id', { key, id, uniqueId, currentDoc: syncDoc, newDocumentId });

		if (!syncDoc || newDocumentId === null || newDocumentId === undefined) return false;

		const data = syncDoc.data;
		const isCurrentlyLocked = data.isLocked && data.lockHolderId !== uniqueId;
		if (isCurrentlyLocked) return false;

		try {
			await syncDoc.set({
				...data,
				documentId: newDocumentId,
				trigger: 'updateDocumentId',
			});
			updateLockState();

			logDebug('useTwilioLock', 'updated document id', { key, id, uniqueId, data: syncDoc.data });

			return true;
		} catch (err) {
			logError('useTwilioLock', 'error while updating document id', err);
			return false;
		}
	};

	return {
		isLocked,
		lockHolder,
		lockHolderId,
		documentId,
		canEdit: isLocked && lockHolderId === id,
		acquireLock,
		updateDocumentId,
		releaseLock,
		initializeDocument,
		handleOnActive,
	};
};

export default useTwilioLock;
